export type CalendarTimeFilter = 'today' | 'week' | 'month';

export interface EventFetchWindow {
  startAfter: string;
  startBefore: string;
}

const MS_PER_DAY = 86_400_000;

/**
 * One-day pad applied to the LOWER bound of every window.
 *
 * The backend compares the stored `startAt` against these bounds as PLAIN
 * STRINGS (object repo: `attributes->>'startAt' >= startAfter`). All-day events
 * store a bare date-only `startAt` (e.g. "2026-06-15"), which sorts lexically
 * BEFORE that same calendar day's local-midnight timestamp — in a negative-UTC
 * zone local midnight serializes to "2026-06-15T07:00:00.000Z", and a string is
 * always less than its own prefix-extension. So an all-day event landing on the
 * FIRST visible day of a view (day view's only day, week view's Sunday, the
 * month grid's first cell) failed `startAt >= startAfter` and was dropped from
 * the fetch entirely — it never reached the all-day band.
 *
 * Padding the lower bound back one day guarantees the bare date sorts at/after
 * `startAfter` in every timezone. The day-precise filter (`getEventsForDate`)
 * re-narrows the result to the exact visible days, so the extra fetched day is
 * never rendered, and the bound stays a valid RFC3339 `date-time` (no OpenAPI
 * contract change).
 */
const LOWER_BOUND_GUARD_DAYS = 1;

/**
 * Returns an ISO-string date window for the given calendar view and anchor
 * date.  The returned range covers every day the user can see in the view (plus
 * a one-day lower-bound guard — see {@link LOWER_BOUND_GUARD_DAYS}), and a
 * 7-day buffer on each side of the month view to include adjacent-month days
 * that appear in the calendar grid.
 */
export function computeEventFetchWindow(date: Date, filter: CalendarTimeFilter): EventFetchWindow {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  if (filter === 'today') {
    return {
      startAfter: new Date(y, m, d - LOWER_BOUND_GUARD_DAYS, 0, 0, 0, 0).toISOString(),
      startBefore: new Date(y, m, d + 1, 0, 0, 0, 0).toISOString(),
    };
  }

  if (filter === 'week') {
    const dayOfWeek = date.getDay(); // 0 = Sunday
    const startOfWeek = new Date(y, m, d - dayOfWeek - LOWER_BOUND_GUARD_DAYS, 0, 0, 0, 0);
    const endOfWeek = new Date(y, m, d - dayOfWeek + 7, 0, 0, 0, 0);
    return {
      startAfter: startOfWeek.toISOString(),
      startBefore: endOfWeek.toISOString(),
    };
  }

  // month: expand ±7 days to capture adjacent-month days shown in the grid
  const startOfMonth = new Date(y, m, 1, 0, 0, 0, 0);
  const startOfNextMonth = new Date(y, m + 1, 1, 0, 0, 0, 0);
  return {
    startAfter: new Date(startOfMonth.getTime() - (7 + LOWER_BOUND_GUARD_DAYS) * MS_PER_DAY).toISOString(),
    startBefore: new Date(startOfNextMonth.getTime() + 7 * MS_PER_DAY).toISOString(),
  };
}
